import React, { useState, useEffect, useMemo } from "react";
import { useRef } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./style.css";
import useFetchProfile from "../../hooks/useFetchProfile";
import logo from "../../assets/images/logo.png";
import cornerPng from "../../assets/images/corner.png";
import logoPng from "../../assets/images/logo.png";
import signaturePng from "../../assets/images/signature.png";

import useSubjectsFetch from "../../hooks/useFetchSubjects";
import axios from "axios";

import JSZip from "jszip";
import { saveAs } from "file-saver";

import html2canvas from "html2canvas";

function Certificate({
  isOpen,
  onClose,
  subjects,
  auth,
  setCommonWarning,
  navigate,
}) {
  const [certificateData, setCertificateData] = useState([]);
  const [certificateDataCompletedTopicsCount, setCertificateDataCompletedTopicsCount] = useState(0);
  const [lastAttemptDate, setLastAttemptDate] = useState("");
  const certificateRef = useRef(null);

  const purchased_subs = useMemo(
    () =>
      subjects.filter((item) => item.is_purchased === 1).map((item) => item.id),
    [subjects]
  );

  const generateReportPDF = async () => {
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentWidth = pageWidth - 2 * margin;

    let y = 0;

    doc.setFillColor(168, 85, 247);
    doc.rect(0, 0, pageWidth, pageHeight * 0.5, "F");

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.setFont("helvetica", "bold");
    doc.text("CERTIFICATE", margin, 50);
    doc.text("OF EXCELLENCE", margin, 75);

    doc.setFontSize(14);
    doc.setFont("helvetica", "normal");
    doc.text("FEFDY Brain Gym", margin, 100);

    doc.setFillColor(255, 255, 255);
    doc.rect(0, pageHeight * 0.5, pageWidth, pageHeight * 0.5, "F");

    doc.setTextColor(50, 50, 50);
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text("Achievement Report", margin, pageHeight * 0.55 + 20);

    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(120, 120, 120);
    doc.text(
      `Generated: ${new Date().toLocaleDateString()}`,
      margin,
      pageHeight * 0.55 + 40
    );

    doc.addPage();
    y = margin;

    if (!certificateData || certificateData.length === 0) {
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(14);
      doc.setFont("helvetica", "italic");
      doc.text("No data available to display.", margin, y);
    } else {
      certificateData.forEach((subject) => {
        const validLevels = subject.levels?.filter((level) => {
          if (!level.topics || level.topics.length === 0) return false;

          return level.topics.every((topic) => {
            if (!topic.subtopics_data || topic.subtopics_data.length === 0)
              return false;

            const hasZeroPercent = topic.subtopics_data.some(
              (sub) => sub.percent === 0
            );

            return !hasZeroPercent;
          });
        });

        if (!validLevels || validLevels.length === 0) {
          return;
        }

        if (y > pageHeight - margin - 50) {
          doc.addPage();
          y = margin;
        }
        doc.setTextColor(168, 85, 247);
        doc.setFontSize(24);
        doc.setFont("helvetica", "bold");
        doc.text(subject.name, margin, y);
        y += 12;

        doc.setDrawColor(168, 85, 247);
        doc.setLineWidth(2);
        doc.line(margin, y, margin + 80, y);
        y += 15;

        validLevels.forEach((level) => {
          if (!level.topics || level.topics.length === 0) {
            return;
          }

          if (y > pageHeight - margin - 50) {
            doc.addPage();
            y = margin;
          }

          doc.setTextColor(100, 100, 100);
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.text(`Level: ${level.name}`, margin, y);
          y += 8;

          level.topics.forEach((topic) => {
            const validSubtopics = topic.subtopics_data?.filter(
              (sub) => sub.percent !== 0
            );

            if (!validSubtopics || validSubtopics.length === 0) {
              return;
            }

            if (y > pageHeight - margin - 50) {
              doc.addPage();
              y = margin;
            }

            doc.setTextColor(80, 80, 80);
            doc.setFontSize(11);
            doc.setFont("helvetica", "normal");
            doc.text(`Topic: ${topic.name}`, margin + 5, y);
            y += 7;

            const tableData = validSubtopics.map((sub) => [
              sub.title,
              sub.percent,
            ]);

            doc.autoTable({
              startY: y,
              head: [["Subtopic Name", "Mark"]],
              body: tableData,
              theme: "striped",
              headStyles: {
                fillColor: [168, 85, 247],
                textColor: [255, 255, 255],
                fontStyle: "bold",
                fontSize: 10,
                padding: 6,
              },
              bodyStyles: { fontSize: 9, padding: 5, textColor: [50, 50, 50] },
              alternateRowStyles: { fillColor: [245, 245, 250] },
              columnStyles: {
                0: { cellWidth: contentWidth * 0.65 },
                1: { cellWidth: contentWidth * 0.35, halign: "center" },
              },
              margin: { left: margin, right: margin },
            });

            y = doc.lastAutoTable.finalY + 10;
          });
        });
      });
    }

    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFillColor(245, 245, 250);
      doc.rect(0, pageHeight - 12, pageWidth, 12, "F");

      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.setFont("helvetica", "normal");
      doc.text(`© 2025 FEFDY Brain Gym`, margin, pageHeight - 6);
      doc.text(
        `Page ${i} of ${totalPages}`,
        pageWidth - margin - 20,
        pageHeight - 6
      );
    }

    return doc.output("blob");
  };

  const generateCertificatePDF = async () => {
    const element = certificateRef.current;
    if (!element) return null;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: null,
    });

    const imgData = canvas.toDataURL("image/png");

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4",
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    const imgWidth = pdfWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    const yOffset = imgHeight < pdfHeight ? (pdfHeight - imgHeight) / 2 : 0;

    pdf.addImage(imgData, "PNG", 0, yOffset, imgWidth, imgHeight);

    return pdf.output("blob");
  };

  const handleDownloadAllPDFs = async () => {
    const zip = new JSZip();

    const certificateBlob = await generateCertificatePDF();
    const reportBlob = await generateReportPDF();

    zip.file("Certificate.pdf", certificateBlob);
    zip.file("Certificate Report.pdf", reportBlob);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    saveAs(zipBlob, "FEFDY-Certificate-Report.zip");
  };

  useEffect(() => {
    const loadCertificateData = async () => {
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}certificate`,
          {
            token: auth.token,
            subjects: purchased_subs,
          }
        );

        if (res.status === 200) {
          setCertificateData(res.data.data);
          const certificateData123 = res.data.data;
          const completedSubjects = certificateData123
            .map((sub) => {
              const completedLevels = sub.levels
                .filter((level) => level.is_completed === 1)
                .map((level) => level.name);

              return completedLevels.length
                ? { subject: sub.name, levels: completedLevels }
                : null;
            })
            .filter(Boolean);
          setCertificateDataCompletedTopicsCount(completedSubjects.length);
          setLastAttemptDate(res.data.last_test_date);
        } else if (res.status === 400) {
          setCommonWarning(res.data.message || "Bad request");
        } else if (res.status === 401) {
          navigate("/login");
        }
      } catch (error) {
        console.error("Error fetching certificate:", error);
        setCommonWarning("Something went wrong while fetching certificate.");
      }
    };

    if (purchased_subs.length > 0 && auth?.token) {
      loadCertificateData();
    }
  }, [purchased_subs, auth, setCommonWarning, navigate]);

  if (!isOpen) return null;

  let allPurchasedSubjects = true;
  let allCompletedSubjects = true;
  let allPurchasedLevels = true;
  let allCompletedLevels = true;

  let completedLevelsPerSubject = [];

  certificateData.forEach((subject) => {
    if (subject.is_purchased !== 1) allPurchasedSubjects = false;
    if (subject.is_completed !== 1) allCompletedSubjects = false;

    const levels = subject.levels;
    let completedLevels = [];

    levels.forEach((level) => {
      if (level.is_purchased !== 1) allPurchasedLevels = false;
      if (level.is_completed !== 1) allCompletedLevels = false;

      if (level.is_completed === 1) completedLevels.push(level.name);
    });

    completedLevelsPerSubject.push(completedLevels);
  });

  let caseOne = false;
  let caseTwo = false;
  let caseThree = false;
  let caseFour = false;

  if (
    allPurchasedSubjects &&
    allCompletedSubjects &&
    allPurchasedLevels &&
    allCompletedLevels
  ) {
    caseOne = true;
  } else {
    const firstSubjectLevels = completedLevelsPerSubject[0] || [];
    const sameLevelsAcrossSubjects = completedLevelsPerSubject.every(
      (levels) =>
        levels.length === firstSubjectLevels.length &&
        levels.every((level) => firstSubjectLevels.includes(level))
    );

    if (firstSubjectLevels.length > 0 && sameLevelsAcrossSubjects) {
      caseThree = true;
    } else if (completedLevelsPerSubject.some((levels) => levels.length > 0)) {
      caseTwo = true;
    } else {
      caseFour = true;
    }
  }

  const calculatedAge = calculateAge(auth.user.dob);

  return (
    <div className="certificate-modal-overlay" onClick={onClose}>
      <div
        className="certificate-modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button className="modal-close-btn" onClick={onClose}>
          ✕
        </button>

        {/* Certificate Content (scaled wrapper) */}
        <div className="certificate-scale-wrapper">
          <div
            className={`certificate ${caseFour ? "certificate-blurred" : ""}`}
            ref={certificateRef}
          >
            <div className="inner-border">
              {/* Corners */}
              <img src={cornerPng} className="corner tl" alt="corner" />
              <img src={cornerPng} className="corner tr" alt="corner" />
              <img src={cornerPng} className="corner bl" alt="corner" />
              <img src={cornerPng} className="corner br" alt="corner" />

              {/* Logo */}
              <div className="logo">
                <img src={logoPng} className="logo-main" alt="FEFDY Logo" />
              </div>

              {/* Header */}
              <div className="certificate-header">
                <div className="title-text">
                  <h1 className="certificate-title">CERTIFICATE</h1>
                  <h2 className="certificate-subtitle">OF EXCELLENCE</h2>
                </div>
              </div>

              {/* Content */}
              <p className="description" style={{ whiteSpace: "pre-line" }}>
                {caseOne && (
                  <span>
                    This certificate is proudly presented to{" "}
                    <span className="recipient-inline">{auth.user.name}</span>,
                    Age <span className="recipient-inline">{calculatedAge}</span>,
                    for successfully completing all eight learning levels
                    (Explorer, Beginner, Learner, Thinker, Challenger, Achiever,
                    Master, and Genius) in the subjects{" "}
                    {certificateData.map((sub) => sub.name).join(", ")} at the
                    Fefdy Brain Gym. Your dedication and hard work throughout
                    every level are truly commendable. This achievement is proudly
                    recognized throughout{" "}
                    <span className="recipient-inline">
                      {new Date().getFullYear()}
                    </span>
                    .
                  </span>
                )}

                {caseTwo && (
                  <span>
                    This certificate is proudly presented to{" "}
                    <span className="recipient-inline">{auth.user.name}</span>,
                    Age <span className="recipient-inline">{calculatedAge}</span>,
                    for completing multiple levels {certificateDataCompletedTopicsCount > 1 ? `across the subjects:` : `in the subject:`}
                    {"\n"}
                    {certificateData
                      .map((sub) => {
                        const completedLevels = sub.levels
                          .filter((level) => level.is_completed === 1)
                          .map((level) => level.name)
                          .join(", ");
                        return completedLevels
                          ? `${sub.name} – ${completedLevels}`
                          : null;
                      })
                      .filter(Boolean)
                      .join("\n")}
                    {"\n"}This achievement is proudly recognized throughout{" "}
                    <span className="recipient-inline">
                      {new Date().getFullYear()}
                    </span>
                    .
                  </span>
                )}

                {caseThree && (
                  <span>
                    This certificate is proudly presented to{" "}
                    <span className="recipient-inline">{auth.user.name}</span>,
                    Age <span className="recipient-inline">{calculatedAge}</span>,
                    for successfully completing the{" "}
                    {completedLevelsPerSubject[0].join(", ")} level
                    {completedLevelsPerSubject[0].length > 1 ? "s" : ""} in the
                    subjects {certificateData.map((sub) => sub.name).join(", ")}.
                    This achievement is proudly recognized throughout{" "}
                    <span className="recipient-inline">
                      {new Date().getFullYear()}
                    </span>
                    .
                  </span>
                )}

                {caseFour && (
                  <span>
                    This certificate is proudly presented to{" "}
                    <span className="recipient-inline">{auth.user.name}</span>,
                    Age <span className="recipient-inline">{calculatedAge}</span>,
                    for starting your journey in the FEFDY Brain Gym. No levels
                    have been completed yet, but your dedication to learning is
                    acknowledged. Keep going, and you will achieve great
                    milestones!
                  </span>
                )}
              </p>

              {/* Footer */}
              <div className="footer_certificate">
                <div className="footer-box">
                  {lastAttemptDate}
                  <div className="footer-line"></div>
                  <span>DATE</span>
                </div>

                <div className="footer-box">
                  <img src={signaturePng} className="signature" alt="Signature" />
                  <div className="footer-line"></div>
                  <span>FOUNDER OF FEFDY</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {caseFour && (
          <div className="certificate-overlay-message">
            <h3>Certificate Not Yet Earned</h3>
            <p>
              No levels have been completed yet, but your dedication to learning
              is acknowledged. Keep going, and you will achieve great
              milestones!
            </p>
          </div>
        )}
        {/* Download Button */}
        {!caseFour && (
          <button
            className="modal-download-btn"
            onClick={handleDownloadAllPDFs}
          ></button>
        )}
      </div>
    </div>
  );
}

function calculateAge(dobString) {
  if (!dobString) return null;
  const dob = new Date(dobString);
  const today = new Date();

  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }

  return age;
}

export default Certificate;